/**
 * Trust Center hub — data-layer client.
 *
 * Thin typed wrapper over `apiClient` that hits nine trust-posture
 * endpoints in parallel:
 *
 *   GET /api/v1/trust-center         — public attestation / entry index
 *   GET /api/v1/soc2-iso             — SOC 2 / ISO 27001 cert list
 *   GET /api/v1/dpa                  — DPA template + download link
 *   GET /api/v1/sub-processors       — published sub-processor list
 *   GET /api/v1/encryption-posture   — at-rest / in-transit posture
 *   GET /api/v1/data-residency       — tenant region + status
 *   GET /api/v1/dpf-registration     — DPF registration status
 *   GET /api/v1/gdpr-dsr             — open GDPR DSR count
 *   GET /api/v1/ccpa-dsr             — open CCPA DSR count
 *   GET /api/v1/pentest              — last pentest report summary
 *
 * The hub renders a tolerant view over whatever JSON lands — each card
 * falls back to a placeholder when a field is missing. This keeps the
 * client loosely coupled to API schema drift so the hub stays useful
 * even if a downstream team tweaks their payload.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// View model — one card per card id. The API shapes are deliberately
// loose: a card renders 2-3 fields extracted by `extract*` helpers below.
// ---------------------------------------------------------------------------

export type TrustCardId =
	| "trust-center"
	| "soc2-iso"
	| "dpa"
	| "sub-processors"
	| "encryption-posture"
	| "data-residency"
	| "dpf-registration"
	| "gdpr-dsr"
	| "ccpa-dsr"
	| "pentest";

export interface TrustCardField {
	label: string;
	value: string;
}

export interface TrustCardView {
	id: TrustCardId;
	title: string;
	description: string;
	fields: TrustCardField[];
	detailsHref: string;
}

// ---------------------------------------------------------------------------
// Loose envelopes — we type each endpoint's *useful* surface only. Extra
// keys are preserved via the index signature so a future payload change
// never breaks the hub.
// ---------------------------------------------------------------------------

export interface TrustCenterPublic {
	entries?: unknown[];
	by_type?: Record<string, unknown[]>;
	tenant_id?: string;
	[k: string]: unknown;
}

export interface Soc2IsoList {
	certifications?: Array<{ name?: string; status?: string; issued_at?: number | string }>;
	items?: unknown[];
	[k: string]: unknown;
}

export interface DpaResponse {
	download_url?: string;
	template_url?: string;
	updated_at?: number | string;
	version?: string;
	[k: string]: unknown;
}

export interface SubProcessorsList {
	sub_processors?: Array<{ name?: string; region?: string; purpose?: string }>;
	items?: unknown[];
	count?: number;
	[k: string]: unknown;
}

export interface EncryptionPosture {
	at_rest?: string | { algorithm?: string; status?: string };
	in_transit?: string | { protocol?: string; status?: string };
	key_rotation_days?: number;
	[k: string]: unknown;
}

export interface DataResidency {
	residency?: {
		region?: string;
		status?: string;
		updated_at?: number | string;
	} | null;
	region?: string;
	[k: string]: unknown;
}

export interface DpfRegistration {
	status?: string;
	registered_at?: number | string;
	organization?: string;
	[k: string]: unknown;
}

export interface DsrSummary {
	open?: number;
	open_count?: number;
	pending?: number;
	total?: number;
	items?: unknown[];
	requests?: unknown[];
	[k: string]: unknown;
}

export interface PentestSummary {
	last_report_date?: number | string;
	last_report_at?: number | string;
	vendor?: string;
	findings_open?: number;
	items?: Array<{ report_date?: number | string; vendor?: string }>;
	[k: string]: unknown;
}

// ---------------------------------------------------------------------------
// Per-endpoint fetchers — 404 / 401 / 403 are treated as "no data" so the
// card shows a "No data yet" fallback instead of crashing the hub.
// ---------------------------------------------------------------------------

type Nullable<T> = T | null;

async function safeGet<T>(path: string): Promise<Nullable<T>> {
	try {
		return await apiClient.get<T>(path);
	} catch (err) {
		if (err instanceof ApiError && [401, 403, 404].includes(err.status)) {
			return null;
		}
		throw err;
	}
}

export function fetchTrustCenter(): Promise<Nullable<TrustCenterPublic>> {
	return safeGet<TrustCenterPublic>("/trust-center/public");
}

export function fetchSoc2Iso(): Promise<Nullable<Soc2IsoList>> {
	return safeGet<Soc2IsoList>("/soc2-iso");
}

export function fetchDpa(): Promise<Nullable<DpaResponse>> {
	return safeGet<DpaResponse>("/dpa");
}

export function fetchSubProcessors(): Promise<Nullable<SubProcessorsList>> {
	return safeGet<SubProcessorsList>("/sub-processors");
}

export function fetchEncryptionPosture(): Promise<Nullable<EncryptionPosture>> {
	return safeGet<EncryptionPosture>("/encryption-posture");
}

export function fetchDataResidency(): Promise<Nullable<DataResidency>> {
	return safeGet<DataResidency>("/data-residency");
}

export function fetchDpfRegistration(): Promise<Nullable<DpfRegistration>> {
	return safeGet<DpfRegistration>("/dpf-registration");
}

export function fetchGdprDsr(): Promise<Nullable<DsrSummary>> {
	return safeGet<DsrSummary>("/gdpr-dsr");
}

export function fetchCcpaDsr(): Promise<Nullable<DsrSummary>> {
	return safeGet<DsrSummary>("/ccpa-dsr");
}

export function fetchPentest(): Promise<Nullable<PentestSummary>> {
	return safeGet<PentestSummary>("/pentest");
}

// ---------------------------------------------------------------------------
// Parallel bundle — every card fires off in parallel; failures in one
// lane do not block the rest. The hub renders a pending state per card
// and swaps to data as each settles.
// ---------------------------------------------------------------------------

export interface TrustHubBundle {
	trustCenter: Nullable<TrustCenterPublic>;
	soc2Iso: Nullable<Soc2IsoList>;
	dpa: Nullable<DpaResponse>;
	subProcessors: Nullable<SubProcessorsList>;
	encryption: Nullable<EncryptionPosture>;
	residency: Nullable<DataResidency>;
	dpf: Nullable<DpfRegistration>;
	gdpr: Nullable<DsrSummary>;
	ccpa: Nullable<DsrSummary>;
	pentest: Nullable<PentestSummary>;
}

export async function fetchTrustHub(): Promise<TrustHubBundle> {
	const [
		trustCenter,
		soc2Iso,
		dpa,
		subProcessors,
		encryption,
		residency,
		dpf,
		gdpr,
		ccpa,
		pentest,
	] = await Promise.all([
		fetchTrustCenter(),
		fetchSoc2Iso(),
		fetchDpa(),
		fetchSubProcessors(),
		fetchEncryptionPosture(),
		fetchDataResidency(),
		fetchDpfRegistration(),
		fetchGdprDsr(),
		fetchCcpaDsr(),
		fetchPentest(),
	]);
	return {
		trustCenter,
		soc2Iso,
		dpa,
		subProcessors,
		encryption,
		residency,
		dpf,
		gdpr,
		ccpa,
		pentest,
	};
}

// ---------------------------------------------------------------------------
// Field extractors — each returns the 2-3 fields the card surfaces. All
// extractors are null-tolerant so the page stays renderable if the API
// is partially down or the tenant has no data yet.
// ---------------------------------------------------------------------------

const MISSING = "—";

function formatDate(value: number | string | undefined | null): string {
	if (value === undefined || value === null || value === "") return MISSING;
	const n = typeof value === "string" ? Number(value) : value;
	const ms = Number.isFinite(n) ? (n < 1e12 ? n * 1000 : n) : Date.parse(String(value));
	if (!Number.isFinite(ms)) return MISSING;
	const d = new Date(ms);
	if (Number.isNaN(d.getTime())) return MISSING;
	return d.toISOString().slice(0, 10);
}

function count(obj: unknown): number | null {
	if (!obj || typeof obj !== "object") return null;
	const rec = obj as Record<string, unknown>;
	for (const key of ["count", "total", "open", "open_count", "pending"]) {
		const v = rec[key];
		if (typeof v === "number" && Number.isFinite(v)) return v;
	}
	for (const key of ["items", "entries", "requests", "sub_processors", "certifications"]) {
		const v = rec[key];
		if (Array.isArray(v)) return v.length;
	}
	return null;
}

export function extractTrustCenterFields(data: Nullable<TrustCenterPublic>): TrustCardField[] {
	if (!data) return [{ label: "Status", value: "No data yet" }];
	const entries = Array.isArray(data.entries) ? data.entries.length : null;
	const types = data.by_type ? Object.keys(data.by_type).length : null;
	return [
		{ label: "Published entries", value: entries === null ? MISSING : String(entries) },
		{ label: "Categories", value: types === null ? MISSING : String(types) },
		{ label: "Visibility", value: "Public" },
	];
}

export function extractSoc2IsoFields(data: Nullable<Soc2IsoList>): TrustCardField[] {
	if (!data) return [{ label: "Status", value: "No data yet" }];
	const certs = Array.isArray(data.certifications) ? data.certifications : [];
	const primary = certs[0];
	return [
		{ label: "Certifications", value: String(certs.length || count(data) || 0) },
		{
			label: "Primary framework",
			value: typeof primary?.name === "string" ? primary.name : MISSING,
		},
		{
			label: "Status",
			value: typeof primary?.status === "string" ? primary.status : "Active",
		},
	];
}

export function extractDpaFields(data: Nullable<DpaResponse>): TrustCardField[] {
	if (!data) return [{ label: "Status", value: "No data yet" }];
	const url = data.download_url ?? data.template_url ?? null;
	return [
		{ label: "Template", value: url ? "Available" : "On request" },
		{ label: "Version", value: typeof data.version === "string" ? data.version : MISSING },
		{ label: "Last updated", value: formatDate(data.updated_at) },
	];
}

export function extractSubProcessorsFields(
	data: Nullable<SubProcessorsList>,
): TrustCardField[] {
	if (!data) return [{ label: "Status", value: "No data yet" }];
	const list = Array.isArray(data.sub_processors) ? data.sub_processors : [];
	const total = list.length || count(data) || 0;
	const regions = new Set(
		list.map((s) => (typeof s.region === "string" ? s.region : null)).filter(Boolean) as string[],
	);
	return [
		{ label: "Sub-processors", value: String(total) },
		{ label: "Distinct regions", value: String(regions.size) },
		{ label: "Published", value: total > 0 ? "Yes" : "No" },
	];
}

export function extractEncryptionFields(
	data: Nullable<EncryptionPosture>,
): TrustCardField[] {
	if (!data) return [{ label: "Status", value: "No data yet" }];
	const atRest =
		typeof data.at_rest === "string"
			? data.at_rest
			: data.at_rest?.algorithm ?? data.at_rest?.status ?? "AES-256";
	const inTransit =
		typeof data.in_transit === "string"
			? data.in_transit
			: data.in_transit?.protocol ?? data.in_transit?.status ?? "TLS 1.3";
	return [
		{ label: "At rest", value: atRest },
		{ label: "In transit", value: inTransit },
		{
			label: "Key rotation",
			value:
				typeof data.key_rotation_days === "number"
					? `${data.key_rotation_days} days`
					: MISSING,
		},
	];
}

export function extractResidencyFields(data: Nullable<DataResidency>): TrustCardField[] {
	if (!data) return [{ label: "Status", value: "No data yet" }];
	const row = data.residency ?? null;
	const region = row?.region ?? data.region ?? MISSING;
	const status = row?.status ?? "Not configured";
	return [
		{ label: "Region", value: typeof region === "string" ? region.toUpperCase() : MISSING },
		{ label: "Status", value: String(status) },
		{ label: "Updated", value: formatDate(row?.updated_at) },
	];
}

export function extractDpfFields(data: Nullable<DpfRegistration>): TrustCardField[] {
	if (!data) return [{ label: "Status", value: "No data yet" }];
	return [
		{ label: "Status", value: typeof data.status === "string" ? data.status : "Pending" },
		{
			label: "Organization",
			value: typeof data.organization === "string" ? data.organization : MISSING,
		},
		{ label: "Registered", value: formatDate(data.registered_at) },
	];
}

export function extractDsrFields(
	data: Nullable<DsrSummary>,
	region: "GDPR" | "CCPA",
): TrustCardField[] {
	if (!data) return [{ label: "Status", value: "No data yet" }];
	const open = count(data) ?? 0;
	return [
		{ label: "Open requests", value: String(open) },
		{ label: "Jurisdiction", value: region === "GDPR" ? "EU / UK" : "California" },
		{ label: "SLA", value: region === "GDPR" ? "30 days" : "45 days" },
	];
}

export function extractPentestFields(data: Nullable<PentestSummary>): TrustCardField[] {
	if (!data) return [{ label: "Status", value: "No data yet" }];
	const latest = data.items?.[0];
	const date = data.last_report_date ?? data.last_report_at ?? latest?.report_date;
	const vendor = data.vendor ?? latest?.vendor;
	return [
		{ label: "Last report", value: formatDate(date) },
		{ label: "Vendor", value: typeof vendor === "string" ? vendor : MISSING },
		{
			label: "Open findings",
			value:
				typeof data.findings_open === "number" ? String(data.findings_open) : MISSING,
		},
	];
}

// ---------------------------------------------------------------------------
// Card catalogue — stable metadata used by the page grid. Each entry is
// paired with its extractor at render time.
// ---------------------------------------------------------------------------

export interface TrustCardMeta {
	id: TrustCardId;
	title: string;
	description: string;
	detailsHref: string;
}

export const TRUST_CARD_META: readonly TrustCardMeta[] = [
	{
		id: "trust-center",
		title: "Trust Center",
		description: "Public attestation and policy index.",
		detailsHref: "#",
	},
	{
		id: "soc2-iso",
		title: "SOC 2 and ISO 27001",
		description: "Independent audit certifications on file.",
		detailsHref: "#",
	},
	{
		id: "dpa",
		title: "Data Processing Agreement",
		description: "Downloadable DPA template for customers.",
		detailsHref: "#",
	},
	{
		id: "sub-processors",
		title: "Sub-processors",
		description: "Published list of third-party processors.",
		detailsHref: "#",
	},
	{
		id: "encryption-posture",
		title: "Encryption posture",
		description: "At-rest and in-transit protections.",
		detailsHref: "#",
	},
	{
		id: "data-residency",
		title: "Data residency",
		description: "Selected storage region for this tenant.",
		detailsHref: "#",
	},
	{
		id: "dpf-registration",
		title: "EU-US Data Privacy Framework",
		description: "DPF registration status.",
		detailsHref: "#",
	},
	{
		id: "gdpr-dsr",
		title: "GDPR data subject requests",
		description: "Open GDPR DSRs for this tenant.",
		detailsHref: "#",
	},
	{
		id: "ccpa-dsr",
		title: "CCPA data subject requests",
		description: "Open CCPA DSRs for this tenant.",
		detailsHref: "#",
	},
	{
		id: "pentest",
		title: "Penetration testing",
		description: "Most recent third-party pentest.",
		detailsHref: "#",
	},
] as const;

export function buildCards(bundle: TrustHubBundle): TrustCardView[] {
	return TRUST_CARD_META.map((meta) => {
		const fields = (() => {
			switch (meta.id) {
				case "trust-center":
					return extractTrustCenterFields(bundle.trustCenter);
				case "soc2-iso":
					return extractSoc2IsoFields(bundle.soc2Iso);
				case "dpa":
					return extractDpaFields(bundle.dpa);
				case "sub-processors":
					return extractSubProcessorsFields(bundle.subProcessors);
				case "encryption-posture":
					return extractEncryptionFields(bundle.encryption);
				case "data-residency":
					return extractResidencyFields(bundle.residency);
				case "dpf-registration":
					return extractDpfFields(bundle.dpf);
				case "gdpr-dsr":
					return extractDsrFields(bundle.gdpr, "GDPR");
				case "ccpa-dsr":
					return extractDsrFields(bundle.ccpa, "CCPA");
				case "pentest":
					return extractPentestFields(bundle.pentest);
			}
		})();
		return {
			id: meta.id,
			title: meta.title,
			description: meta.description,
			fields,
			detailsHref: meta.detailsHref,
		};
	});
}

export const trustHubQueryKeys = {
	root: ["trust-hub"] as const,
};
